import { Chord } from 'chordsheetjs';

// "Łatwiejsze chwyty": podpowiedź pozycji kapodastera, która maksymalizuje
// liczbę łatwych chwytów otwartych w utworze. Osobny moduł od
// `render/transpozycja.ts` - ten liczy tylko OCENĘ pozycji kapodastera po
// nazwach akordów, nic nie renderuje i nic nie zna o `Utwor`/`ChartVariant`.
// UI (`ui/stroj.ts`) dostarcza listę unikalnych akordów utworu i bieżący
// kapodaster, ten moduł zwraca gotowe propozycje do pokazania.

/** Pozycja kapodastera zaproponowana grającemu, z podglądem akordów, jakie
 * wyjdą po przesunięciu na tę pozycję. */
export interface CapoSuggestion {
  capo: number;
  /** Ułamek unikalnych akordów utworu, które po przesunięciu na `capo` trafiają
   * w łatwy chwyt otwarty (0..1). */
  score: number;
  /** Unikalne nazwy akordów PO transpozycji na `capo`, w kolejności `uniqueChords`. */
  chords: string[];
}

const MIN_CAPO = 0;
const MAX_CAPO = 7;

/** Łatwe chwyty otwarte na gitarze, dostępne bez barre. `F` jest tu
 * świadomie, choć w wielu metodach nauki gra się go jako mini-barre - to
 * wciąż najprostszy chwyt durowy poza tą listą, więc liczy się jako trafienie,
 * nie jak reszta akordów barowych (Bm, F#m, Bb...), które w tym zbiorze w
 * ogóle nie występują i przez to nigdy nie trafiają w wynik - to jest cała
 * "kara za barre" z tej funkcji: brak miejsca na liście, nie ułamkowa waga. */
const LATWE_CHWYTY: ReadonlySet<string> = new Set(['C', 'G', 'D', 'A', 'E', 'Am', 'Em', 'Dm', 'F']);

/** Transponuje JEDEN token akordu o `delta` półtonów przez `Chord` z
 * ChordSheetJS, jak `render/transpozycja.ts`. Token nierozpoznany (śmieć w
 * źródle) wraca BEZ ZMIAN - nigdy nie zgadujemy, a token, który nie
 * parsuje się, i tak nie trafi w `LATWE_CHWYTY`. */
function transponujNazwe(token: string, delta: number): string {
  const akord = Chord.parse(token.trim());
  if (akord === null) return token;
  return akord.transpose(delta).toString();
}

/** Ocena JEDNEJ pozycji kapodastera: akordy utworu transponowane W DÓŁ o
 * `capo` półtonów - to jest to, co gra ręka, gdy kapodaster podnosi
 * brzmienie o `capo` progów, a grający chce zagrać chwyt niższy o tyle samo. */
function ocenPozycje(uniqueChords: readonly string[], capo: number): CapoSuggestion {
  const chords = uniqueChords.map((token) => transponujNazwe(token, -capo));
  const easyCount = chords.filter((nazwa) => LATWE_CHWYTY.has(nazwa)).length;
  const score = uniqueChords.length > 0 ? easyCount / uniqueChords.length : 0;
  return { capo, score, chords };
}

/**
 * Propozycje pozycji kapodastera dla `uniqueChords` (unikalne nazwy akordów
 * utworu, w oryginalnej wysokości), pomijając `currentCapo` i każdą pozycję,
 * która nie jest LEPSZA niż obecna (ściśle większy `score`, nie tylko równy -
 * remis niczego by nie poprawił grającemu). Zwraca do trzech najlepszych,
 * posortowanych malejąco po `score`, a przy remisie rosnąco po `capo`
 * (niższy kapodaster to mniej do przestawienia).
 */
export function suggestCapo(uniqueChords: string[], currentCapo: number): CapoSuggestion[] {
  if (uniqueChords.length === 0) return [];

  const biezaca = ocenPozycje(uniqueChords, currentCapo);
  const kandydaci: CapoSuggestion[] = [];

  for (let capo = MIN_CAPO; capo <= MAX_CAPO; capo += 1) {
    if (capo === currentCapo) continue;
    const ocena = ocenPozycje(uniqueChords, capo);
    if (ocena.score <= biezaca.score) continue;
    kandydaci.push(ocena);
  }

  kandydaci.sort((a, b) => b.score - a.score || a.capo - b.capo);
  return kandydaci.slice(0, 3);
}
